/**
 * Spanish translations for intake form labels, options, and UI text.
 * Used by IntakePage when patient selects Español.
 * Falls back to English if no translation exists.
 */

export const UI_TRANSLATIONS = {
  en: {
    selectLanguage: 'Language',
    formOf: 'Form {current} of {total}',
    formsFor: 'Forms for',
    next: 'Next',
    back: 'Back',
    submitAll: 'Submit All Forms',
    submitting: 'Submitting...',
    required: 'Required',
    selectOption: 'Select...',
    signHere: 'Sign above using your finger or stylus',
    printName: 'Print Name',
    signature: 'Signature',
    clear: 'Clear',
    yes: 'Yes',
    no: 'No',
    patients: 'patients',
  },
  es: {
    selectLanguage: 'Idioma',
    formOf: 'Formulario {current} de {total}',
    formsFor: 'Formularios para',
    next: 'Siguiente',
    back: 'Atrás',
    submitAll: 'Enviar todos los formularios',
    submitting: 'Enviando...',
    required: 'Obligatorio',
    selectOption: 'Seleccionar...',
    signHere: 'Firme arriba usando su dedo o lápiz',
    printName: 'Nombre completo',
    signature: 'Firma',
    clear: 'Borrar',
    yes: 'Sí',
    no: 'No',
    patients: 'pacientes',
  },
};

// Common medical/intake form field label translations
export const LABEL_TRANSLATIONS = {
  // Registration
  'First Name': 'Nombre',
  'Last Name': 'Apellido',
  'Middle Name': 'Segundo nombre',
  'Preferred Name': 'Nombre preferido',
  'Date of Birth': 'Fecha de nacimiento',
  'Gender': 'Género',
  'Marital Status': 'Estado civil',
  'Social Security Number': 'Número de seguro social',
  'Email': 'Correo electrónico',
  'Phone (Cell)': 'Teléfono (celular)',
  'Phone (Home)': 'Teléfono (casa)',
  'Phone (Work)': 'Teléfono (trabajo)',
  'Preferred Contact Method': 'Método de contacto preferido',
  'Address': 'Dirección',
  'Address Line 1': 'Dirección línea 1',
  'Address Line 2': 'Dirección línea 2',
  'City': 'Ciudad',
  'State': 'Estado',
  'ZIP Code': 'Código postal',
  'ZIP': 'Código postal',
  'Employer Name': 'Nombre del empleador',
  'Employer Phone': 'Teléfono del empleador',
  'Occupation': 'Ocupación',
  'Emergency Contact Name': 'Nombre del contacto de emergencia',
  'Emergency Contact Phone': 'Teléfono del contacto de emergencia',
  'Emergency Contact Relationship': 'Relación del contacto de emergencia',
  'Referral Source': 'Fuente de referencia',
  'Payment Type': 'Tipo de pago',

  // Health History
  'What is the primary reason for your visit today? Please describe your symptoms in detail.': '¿Cuál es la razón principal de su visita hoy? Por favor describa sus síntomas en detalle.',
  'Where is your pain? (check all that apply)': '¿Dónde tiene dolor? (marque todo lo que aplique)',
  'Describe your pain (check all that apply)': 'Describa su dolor (marque todo lo que aplique)',
  'How long have you had this problem?': '¿Cuánto tiempo ha tenido este problema?',
  'How did this problem start?': '¿Cómo comenzó este problema?',
  'If related to an injury or incident, please describe what happened': 'Si está relacionado con una lesión o incidente, por favor describa lo que sucedió',
  'Does your pain radiate or travel to another area?': '¿Su dolor se irradia o viaja a otra área?',
  'If yes, where does it radiate to?': 'Si es así, ¿a dónde se irradia?',
  'Overall, is your condition:': 'En general, su condición está:',
  'What makes your symptoms WORSE?': '¿Qué empeora sus síntomas?',
  'What makes your symptoms BETTER?': '¿Qué mejora sus síntomas?',
  'Does this condition interfere with:': '¿Esta condición interfiere con:',
  'Previous treatment for this condition': 'Tratamiento previo para esta condición',
  'Was previous treatment helpful?': '¿El tratamiento previo fue útil?',
  'Are you currently taking any medications?': '¿Está tomando medicamentos actualmente?',
  'Do you have any allergies?': '¿Tiene alguna alergia?',
  'Are you taking blood thinners (Coumadin, Eliquis, aspirin daily)?': '¿Está tomando anticoagulantes (Coumadin, Eliquis, aspirina diaria)?',
  'Pacemaker or Metal Implants': 'Marcapasos o implantes metálicos',
  'Currently Pregnant': 'Actualmente embarazada',
  'Have you received chiropractic care before?': '¿Ha recibido atención quiropráctica antes?',
  'Overall stress level': 'Nivel de estrés general',
  'Current Prescription Medications (name, dose, frequency)': 'Medicamentos recetados actuales (nombre, dosis, frecuencia)',
  'Over-the-Counter Medications Used Regularly': 'Medicamentos de venta libre que usa regularmente',
  'Vitamins / Supplements': 'Vitaminas / Suplementos',
  'Medication Allergies and Reactions': 'Alergias a medicamentos y reacciones',
  'Food Allergies': 'Alergias alimentarias',
  'Previous Surgeries (list with approximate dates)': 'Cirugías anteriores (liste con fechas aproximadas)',
  'Previous Injuries or Accidents': 'Lesiones o accidentes anteriores',

  // Pain locations
  'Head / Jaw': 'Cabeza / Mandíbula',
  'Neck': 'Cuello',
  'Upper Back': 'Espalda superior',
  'Mid Back': 'Espalda media',
  'Lower Back': 'Espalda baja',
  'Left Shoulder': 'Hombro izquierdo',
  'Right Shoulder': 'Hombro derecho',
  'Left Hip': 'Cadera izquierda',
  'Right Hip': 'Cadera derecha',
  'Left Knee': 'Rodilla izquierda',
  'Right Knee': 'Rodilla derecha',
  'Chest': 'Pecho',
  'Abdomen': 'Abdomen',

  // Pain qualities
  'Sharp': 'Agudo',
  'Dull': 'Sordo',
  'Aching': 'Dolor persistente',
  'Burning': 'Ardor',
  'Throbbing': 'Pulsante',
  'Stabbing': 'Punzante',
  'Shooting': 'Lancinante',
  'Numbness': 'Entumecimiento',
  'Tingling': 'Hormigueo',
  'Radiating': 'Irradiante',
  'Stiffness': 'Rigidez',
  'Tightness': 'Tensión',
  'Constant': 'Constante',
  'Intermittent': 'Intermitente',

  // Common options
  'Yes': 'Sí',
  'No': 'No',
  'Male': 'Masculino',
  'Female': 'Femenino',
  'Other': 'Otro',
  'Prefer not to say': 'Prefiero no decir',
  'Single': 'Soltero/a',
  'Married': 'Casado/a',
  'Divorced': 'Divorciado/a',
  'Widowed': 'Viudo/a',
  'Separated': 'Separado/a',
  'Currently': 'Actualmente',
  'Past': 'Pasado',
  'Never': 'Nunca',
  'N/A': 'N/A',
  'Good': 'Bueno',
  'Fair': 'Regular',
  'Poor': 'Malo',
  'Low': 'Bajo',
  'Moderate': 'Moderado',
  'High': 'Alto',
  'Very High': 'Muy alto',
  'Getting Better': 'Mejorando',
  'Getting Worse': 'Empeorando',
  'Staying the Same': 'Igual',
  'Comes and Goes': 'Va y viene',

  // Duration
  'Less than 1 week': 'Menos de 1 semana',
  '1-2 weeks': '1-2 semanas',
  '2-4 weeks': '2-4 semanas',
  '1-3 months': '1-3 meses',
  '3-6 months': '3-6 meses',
  '6-12 months': '6-12 meses',
  '1-2 years': '1-2 años',
  'Over 2 years': 'Más de 2 años',

  // Onset
  'Gradually over time': 'Gradualmente con el tiempo',
  'Suddenly / Acute onset': 'De repente / Inicio agudo',
  'After a car accident': 'Después de un accidente de auto',
  'After a work injury': 'Después de una lesión laboral',
  'After a fall': 'Después de una caída',
  'After a sports injury': 'Después de una lesión deportiva',
  'After surgery': 'Después de una cirugía',
  'During pregnancy': 'Durante el embarazo',
  'Unknown / No specific event': 'Desconocido / Sin evento específico',

  // Lifestyle
  'Work': 'Trabajo',
  'Sleep': 'Sueño',
  'Daily Activities': 'Actividades diarias',
  'Exercise / Hobbies': 'Ejercicio / Pasatiempos',
  'Driving': 'Conducir',
  'Household Chores': 'Tareas del hogar',
  'Caring for Children / Family': 'Cuidado de niños / familia',
  'Emotional Well-Being': 'Bienestar emocional',
  'Relationships': 'Relaciones',

  // Section headers
  'Section A: Reason for Visit': 'Sección A: Razón de su visita',
  'Section B: Current & Past Health Conditions': 'Sección B: Condiciones de salud actuales y pasadas',
  'Section C: Past Medical History': 'Sección C: Historial médico pasado',
  'Section D: Medications & Allergies': 'Sección D: Medicamentos y alergias',
  'Section E: Family History': 'Sección E: Historial familiar',
  'Section F: Lifestyle & Social History': 'Sección F: Estilo de vida e historial social',
  'Section G: Review of Systems': 'Sección G: Revisión de sistemas',
  'Section H: Treatment Goals': 'Sección H: Metas de tratamiento',
  'Section I: For Female Patients': 'Sección I: Para pacientes femeninas',
  // ===== PATIENT REGISTRATION =====
  'Patient Information': 'Información del Paciente',
  'Middle Initial': 'Inicial del Segundo Nombre',
  'Preferred Name / Nickname': 'Nombre Preferido / Apodo',
  'Sex': 'Sexo',
  'SSN (Last 4 Digits)': 'SSN (Últimos 4 Dígitos)',
  'Preferred Language': 'Idioma Preferido',
  "Driver's License Number": 'Número de Licencia de Conducir',
  'DL State': 'Estado de la Licencia',
  'Contact Information': 'Información de Contacto',
  'Home Address': 'Dirección del Hogar',
  'Cell Phone': 'Teléfono Celular',
  'Work Phone': 'Teléfono del Trabajo',
  'Email Address': 'Correo Electrónico',
  'May we leave a voicemail regarding your care?': '¿Podemos dejarle un mensaje de voz sobre su atención?',
  'May we send text message reminders?': '¿Podemos enviarle recordatorios por mensaje de texto?',
  'May we send email communications?': '¿Podemos enviarle comunicaciones por correo electrónico?',
  'Emergency Contact': 'Contacto de Emergencia',
  'Relationship to Patient': 'Relación con el Paciente',
  'Employment Information': 'Información de Empleo',
  'Employment Status': 'Estado de Empleo',
  'Occupation / Job Title': 'Ocupación / Título del Puesto',
  'Employer Address': 'Dirección del Empleador',
  'Primary Insurance': 'Seguro Primario',
  'Insurance Company Name': 'Nombre de la Compañía de Seguros',
  'Insurance Company Phone': 'Teléfono de la Compañía de Seguros',
  'Member / Subscriber ID': 'ID del Miembro / Suscriptor',
  'Group Number': 'Número de Grupo',
  'Primary Subscriber Name (if not patient)': 'Nombre del Suscriptor Principal (si no es el paciente)',
  'Subscriber Date of Birth (if not patient)': 'Fecha de Nacimiento del Suscriptor (si no es el paciente)',
  'Relationship to Subscriber': 'Relación con el Suscriptor',
  'Insurance Card — FRONT (take a photo or upload)': 'Tarjeta de Seguro — FRENTE (tome una foto o suba una imagen)',
  'Insurance Card — BACK (take a photo or upload)': 'Tarjeta de Seguro — REVERSO (tome una foto o suba una imagen)',
  'Secondary Insurance (if applicable)': 'Seguro Secundario (si aplica)',
  'Do you have secondary insurance?': '¿Tiene seguro secundario?',
  'Secondary Insurance Company': 'Compañía de Seguro Secundario',
  'Secondary Member ID': 'ID de Miembro Secundario',
  'Secondary Group Number': 'Número de Grupo Secundario',
  'Secondary Subscriber Name': 'Nombre del Suscriptor Secundario',
  'Responsible Party (if different from patient)': 'Parte Responsable (si es diferente del paciente)',
  'Is the patient the responsible party for billing?': '¿Es el paciente la parte responsable de la facturación?',
  'Responsible Party Name': 'Nombre de la Parte Responsable',
  'Responsible Party Phone': 'Teléfono de la Parte Responsable',
  'How Did You Hear About Us?': '¿Cómo Se Enteró de Nosotros?',
  'Referral Details': 'Detalles de la Referencia',

  // ===== HEALTH CONDITIONS =====
  'Headaches / Migraines': 'Dolores de Cabeza / Migrañas',
  'Neck Pain': 'Dolor de Cuello',
  'Upper / Mid Back Pain': 'Dolor de Espalda Alta / Media',
  'Low Back Pain': 'Dolor de Espalda Baja',
  'Shoulder Pain (Left)': 'Dolor de Hombro (Izquierdo)',
  'Shoulder Pain (Right)': 'Dolor de Hombro (Derecho)',
  'Hip Pain (Left)': 'Dolor de Cadera (Izquierda)',
  'Hip Pain (Right)': 'Dolor de Cadera (Derecha)',
  'Knee Pain': 'Dolor de Rodilla',
  'Arm / Hand Numbness or Tingling': 'Adormecimiento u Hormigueo en Brazo / Mano',
  'Leg / Foot Numbness or Tingling': 'Adormecimiento u Hormigueo en Pierna / Pie',
  'Sciatica': 'Ciática',
  'Carpal Tunnel Syndrome': 'Síndrome del Túnel Carpiano',
  'TMJ / Jaw Pain': 'ATM / Dolor de Mandíbula',
  'Dizziness / Vertigo': 'Mareos / Vértigo',
  'Chronic Fatigue': 'Fatiga Crónica',
  'Sleep Problems / Insomnia': 'Problemas de Sueño / Insomnio',
  'Anxiety': 'Ansiedad',
  'Depression': 'Depresión',
  'High Blood Pressure': 'Presión Arterial Alta',
  'Diabetes (Type 1 or 2)': 'Diabetes (Tipo 1 o 2)',
  'Heart Disease': 'Enfermedad del Corazón',
  'Stroke': 'Derrame Cerebral',
  'Cancer': 'Cáncer',
  'If cancer, type and year diagnosed': 'Si tiene cáncer, tipo y año de diagnóstico',
  'Osteoporosis / Osteopenia': 'Osteoporosis / Osteopenia',
  'Arthritis (Osteo or Rheumatoid)': 'Artritis (Osteo o Reumatoide)',
  'Fibromyalgia': 'Fibromialgia',
  'Thyroid Disorder': 'Trastorno de Tiroides',
  'Asthma / COPD / Breathing Problems': 'Asma / EPOC / Problemas Respiratorios',
  'Seizures / Epilepsy': 'Convulsiones / Epilepsia',
  'Blood Clotting Disorder': 'Trastorno de Coagulación',
  'Kidney Disease': 'Enfermedad Renal',
  'Liver Disease': 'Enfermedad del Hígado',
  'Digestive Problems (IBS, GERD, etc.)': 'Problemas Digestivos (SII, ERGE, etc.)',
  'Autoimmune Disease': 'Enfermedad Autoinmune',
  'If autoimmune, type': 'Si es autoinmune, tipo',
  'Herniated / Bulging Disc': 'Disco Herniado / Abultado',
  'Spinal Stenosis': 'Estenosis Espinal',
  'Scoliosis': 'Escoliosis',
  'Other conditions not listed above': 'Otras condiciones no mencionadas arriba',

  // ===== MEDICAL HISTORY =====
  'Previous Hospitalizations (other than surgeries)': 'Hospitalizaciones Previas (aparte de cirugías)',
  'Previous Fractures or Broken Bones': 'Fracturas o Huesos Rotos Previos',
  'If yes, where and approximately when?': 'Si es sí, ¿dónde y aproximadamente cuándo?',
  'Was previous chiropractic care helpful?': '¿Fue útil el tratamiento quiropráctico anterior?',
  'Chiropractic techniques used (if known)': 'Técnicas quiroprácticas utilizadas (si las conoce)',
  'Previous Physical Therapy?': '¿Terapia Física Previa?',
  'If yes, where, when, and for what?': 'Si es sí, ¿dónde, cuándo y por qué?',
  'Previous injections (cortisone, epidural, trigger point)?': '¿Inyecciones previas (cortisona, epidural, punto gatillo)?',
  'If yes, where and when?': 'Si es sí, ¿dónde y cuándo?',
  'Previous imaging for this condition?': '¿Estudios de imagen previos para esta condición?',
  'Imaging results (if known)': 'Resultados de imagen (si los conoce)',
  'Do you have imaging films or reports to bring?': '¿Tiene películas o reportes de imagen para traer?',
  'Latex Allergy?': '¿Alergia al Látex?',
  'Other Allergies (environmental, seasonal, etc.)': 'Otras Alergias (ambientales, estacionales, etc.)',
  'Any adverse reaction to anesthesia?': '¿Alguna reacción adversa a la anestesia?',
  'If yes, describe the reaction': 'Si es sí, describa la reacción',

  // ===== LIFESTYLE =====
  'Tobacco Use': 'Uso de Tabaco',
  'If current/former: how much and for how many years?': 'Si actual/anterior: ¿cuánto y por cuántos años?',
  'Vaping / E-Cigarette Use': 'Uso de Vapeo / Cigarrillo Electrónico',
  'Alcohol Use': 'Consumo de Alcohol',
  'If yes, approximate drinks per week': 'Si es sí, bebidas aproximadas por semana',
  'Recreational Drug Use': 'Uso de Drogas Recreativas',
  'Caffeine Intake (coffee, tea, energy drinks)': 'Consumo de Cafeína (café, té, bebidas energéticas)',
  'Do you exercise regularly?': '¿Hace ejercicio regularmente?',
  'Types of exercise': 'Tipos de ejercicio',
  'Physical demands of your work': 'Exigencias físicas de su trabajo',
  'Hours sitting per day': 'Horas sentado(a) por día',
  'Hours on computer/phone per day': 'Horas en computadora/teléfono por día',
  'Hobbies / Sports / Activities': 'Pasatiempos / Deportes / Actividades',
  'Hours of sleep per night': 'Horas de sueño por noche',
  'Sleep quality': 'Calidad del sueño',
  'Usual sleep position': 'Posición habitual para dormir',
  'Do you wake with pain or stiffness?': '¿Se despierta con dolor o rigidez?',
  'Primary sources of stress': 'Principales fuentes de estrés',
  'Water intake per day': 'Consumo de agua por día',

  // ===== REVIEW OF SYSTEMS =====
  'General / Constitutional': 'General / Constitucional',
  'Head / Eyes / Ears / Nose / Throat': 'Cabeza / Ojos / Oídos / Nariz / Garganta',
  'Cardiovascular': 'Cardiovascular',
  'Respiratory': 'Respiratorio',
  'Gastrointestinal': 'Gastrointestinal',
  'Genitourinary': 'Genitourinario',
  'Musculoskeletal (other than chief complaint)': 'Musculoesquelético (aparte de la queja principal)',
  'Neurological': 'Neurológico',
  'Skin': 'Piel',
  'Psychiatric': 'Psiquiátrico',
  'Endocrine': 'Endocrino',
  'No symptoms to report in any of the above systems': 'No hay síntomas que reportar en ninguno de los sistemas anteriores',

  // ===== TREATMENT GOALS =====
  'What are your goals for care?': '¿Cuáles son sus metas para el tratamiento?',
  'Other goals': 'Otras metas',
  'How many visits per week can you commit to?': '¿A cuántas visitas por semana puede comprometerse?',
  'Are you interested in home exercises / stretches?': '¿Le interesan ejercicios / estiramientos para hacer en casa?',

  // ===== FEMALE PATIENTS =====
  'Are you pregnant or could you be pregnant?': '¿Está embarazada o podría estar embarazada?',
  'If yes, how many weeks/months?': 'Si es sí, ¿cuántas semanas/meses?',
  'Date of Last Menstrual Period': 'Fecha de la Última Menstruación',
  'Are you nursing/breastfeeding?': '¿Está amamantando?',
  'Number of pregnancies / deliveries': 'Número de embarazos / partos',
  'Are you on birth control?': '¿Usa anticonceptivos?',
  'Have you been diagnosed with osteoporosis?': '¿Le han diagnosticado osteoporosis?',
  'Have you had a DEXA bone density scan?': '¿Se ha hecho una densitometría ósea (DEXA)?',
  'Are you on hormone replacement therapy?': '¿Está en terapia de reemplazo hormonal?',
  'Are you currently pregnant or suspect you may be pregnant?': '¿Está actualmente embarazada o sospecha que puede estar embarazada?',

  // ===== ACCIDENT / PI =====
  'Accident Information': 'Información del Accidente',
  'Date of Accident': 'Fecha del Accidente',
  'Approximate Time': 'Hora Aproximada',
  'Type of Accident': 'Tipo de Accidente',
  'Was a police report filed?': '¿Se presentó un reporte policial?',
  'Police Report Number': 'Número del Reporte Policial',
  'Were you at fault?': '¿Fue usted el culpable?',
  'Your Vehicle (year, make, model)': 'Su Vehículo (año, marca, modelo)',
  'Other Vehicle (year, make, model)': 'Otro Vehículo (año, marca, modelo)',
  'Number of vehicles involved': 'Número de vehículos involucrados',
  'Direction of Impact': 'Dirección del Impacto',
  'Your Position in Vehicle': 'Su Posición en el Vehículo',
  'Seatbelt worn?': '¿Llevaba puesto el cinturón de seguridad?',
  'Seatbelt / restraint worn?': '¿Llevaba puesto el cinturón o sistema de retención?',
  'Car seat (infant/child)': 'Asiento de seguridad (bebé/niño)',
  'Airbags deploy?': '¿Se activaron las bolsas de aire?',
  'Headrest properly positioned?': '¿Estaba el reposacabezas bien posicionado?',
  'Head position at impact': 'Posición de la cabeza al momento del impacto',
  'Did you see the collision coming?': '¿Vio venir la colisión?',
  'Were you bracing for impact?': '¿Se preparó para el impacto?',
  'Was your vehicle moving or stopped?': '¿Su vehículo estaba en movimiento o detenido?',
  'Estimated speed at impact (mph)': 'Velocidad estimada al impacto (mph)',
  'Damage to your vehicle': 'Daño a su vehículo',
  'Were you able to walk away from the scene?': '¿Pudo caminar y retirarse de la escena?',
  'Did you lose consciousness?': '¿Perdió el conocimiento?',
  'If yes, for how long?': 'Si es sí, ¿por cuánto tiempo?',
  'Were there passengers in your vehicle?': '¿Había pasajeros en su vehículo?',
  'Were passengers injured?': '¿Se lesionaron los pasajeros?',
  'Emergency Treatment': 'Tratamiento de Emergencia',
  'Did you go to the ER or urgent care?': '¿Fue a la sala de emergencias o atención urgente?',
  'Hospital / ER Name': 'Nombre del Hospital / Sala de Emergencias',
  'Date of ER Visit': 'Fecha de la Visita a Emergencias',
  'Symptoms After the Accident': 'Síntomas Después del Accidente',
  'When did symptoms first appear?': '¿Cuándo aparecieron los síntomas por primera vez?',
  'Are your symptoms currently:': 'Sus síntomas actualmente están:',
  'Current Pain Level (0-10)': 'Nivel de Dolor Actual (0-10)',
  'Pain Level at Time of Accident (0-10)': 'Nivel de Dolor al Momento del Accidente (0-10)',
  'Worst Pain Since Accident (0-10)': 'Peor Dolor Desde el Accidente (0-10)',
  'Impact on Daily Life': 'Impacto en la Vida Diaria',
  'Have you missed work due to this accident?': '¿Ha faltado al trabajo debido a este accidente?',
  'Dates missed from work': 'Fechas que faltó al trabajo',
  'Total days missed': 'Total de días perdidos',
  'Are you currently able to work?': '¿Actualmente puede trabajar?',
  'Activities you can no longer perform since the accident': 'Actividades que ya no puede realizar desde el accidente',
  'Do you need help with daily activities?': '¿Necesita ayuda con las actividades diarias?',
  'Have you seen any other providers since the accident?': '¿Ha visto a otros proveedores desde el accidente?',
  'Are you currently receiving treatment elsewhere?': '¿Actualmente está recibiendo tratamiento en otro lugar?',
  'Did you have similar symptoms before this accident?': '¿Tenía síntomas similares antes de este accidente?',

  // ===== ATTORNEY =====
  'Do you have an attorney?': '¿Tiene un abogado?',
  'Attorney Name': 'Nombre del Abogado',
  'Law Firm': 'Bufete de Abogados',
  'Attorney Phone': 'Teléfono del Abogado',
  'Attorney Fax': 'Fax del Abogado',
  'Attorney Email': 'Correo Electrónico del Abogado',
  'Paralegal / Case Manager Name': 'Nombre del Paralegal / Administrador del Caso',
  'Paralegal Phone': 'Teléfono del Paralegal',
  'Attorney Case / File Number': 'Número de Caso / Expediente del Abogado',
  "At-Fault Party's Insurance Company": 'Compañía de Seguros de la Parte Culpable',
  'Claim Number': 'Número de Reclamo',
  'Adjuster Name': 'Nombre del Ajustador',
  'Adjuster Phone': 'Teléfono del Ajustador',
  'Adjuster Email': 'Correo Electrónico del Ajustador',
  'Your Auto Insurance Company (for MedPay/PIP)': 'Su Compañía de Seguro de Auto (para MedPay/PIP)',
  'Your Auto Policy Number': 'Número de Póliza de su Auto',
  'MedPay Limit (if known)': 'Límite de MedPay (si lo conoce)',

  // ===== LIEN =====
  'Patient Full Legal Name': 'Nombre Legal Completo del Paciente',
  'Patient Date of Birth': 'Fecha de Nacimiento del Paciente',
  'Patient Address': 'Dirección del Paciente',
  'Law Firm Name': 'Nombre del Bufete de Abogados',
  'Law Firm Address': 'Dirección del Bufete de Abogados',
  'Date of Injury / Accident': 'Fecha de la Lesión / Accidente',
  'Type of Injury': 'Tipo de Lesión',
  'Brief Description of Injury/Accident': 'Breve Descripción de la Lesión/Accidente',
  'At-Fault Party (if known)': 'Parte Culpable (si se conoce)',
  "At-Fault Party's Insurance Carrier (if known)": 'Aseguradora de la Parte Culpable (si se conoce)',
  'Insurance Claim Number (if assigned)': 'Número de Reclamo de Seguro (si fue asignado)',
  'Attorney Georgia Bar Number': 'Número del Colegio de Abogados de Georgia',

  // ===== RECORDS RELEASE =====
  'Release Records To (Name / Organization)': 'Enviar Registros A (Nombre / Organización)',
  'Address / Fax Number': 'Dirección / Número de Fax',
  'Purpose': 'Propósito',
  'Information to Release': 'Información a Divulgar',
  'Date Range': 'Rango de Fechas',

  // ===== MINOR =====
  'Minor Patient Name': 'Nombre del Paciente Menor',
  'Minor Date of Birth': 'Fecha de Nacimiento del Menor',
  'Parent / Legal Guardian Name': 'Nombre del Padre / Tutor Legal',
  'Relationship': 'Relación',
  'Guardian Phone': 'Teléfono del Tutor',
  'Guardian Email': 'Correo Electrónico del Tutor',

  // ===== WORKERS COMP =====
  'Date of Injury': 'Fecha de la Lesión',
  'Time of Injury': 'Hora de la Lesión',
  'Describe in detail how the injury occurred': 'Describa en detalle cómo ocurrió la lesión',
  'Body Parts Injured': 'Partes del Cuerpo Lesionadas',
  'Was the injury reported to your employer?': '¿Se reportó la lesión a su empleador?',
  'Date Reported': 'Fecha del Reporte',
  'Were there witnesses?': '¿Hubo testigos?',
  'Was a First Report of Injury filed?': '¿Se presentó un Primer Reporte de Lesión?',
  'Did you go to ER/doctor after injury?': '¿Fue al hospital/doctor después de la lesión?',
  'Date of first medical treatment': 'Fecha del primer tratamiento médico',
  'Was a drug/alcohol test administered?': '¿Le hicieron una prueba de drogas/alcohol?',
  'How were you referred to our office?': '¿Cómo fue referido a nuestra oficina?',
  'Are you currently working?': '¿Está trabajando actualmente?',
  'Receiving TTD (Temporary Total Disability) benefits?': '¿Recibe beneficios de ITT (Incapacidad Total Temporal)?',
  'Have you been placed on work restrictions?': '¿Le han puesto restricciones de trabajo?',
  'If yes, what are the restrictions?': 'Si es sí, ¿cuáles son las restricciones?',
  'Is your employer accommodating the restrictions?': '¿Su empleador está respetando las restricciones?',
  'Supervisor Name': 'Nombre del Supervisor',
  'Supervisor Phone': 'Teléfono del Supervisor',
  'Your Job Title': 'Su Título de Puesto',
  'How long at this employer?': '¿Cuánto tiempo con este empleador?',
  'Employment type': 'Tipo de empleo',
  'Physical demand level': 'Nivel de exigencia física',
  'Brief description of typical job duties': 'Breve descripción de sus funciones típicas',
  'Frequent lifting required?': '¿Se requiere levantar objetos frecuentemente?',
  'If yes, typical weight lifted': 'Si es sí, peso típico levantado',
  'Frequent bending/stooping?': '¿Se requiere agacharse frecuentemente?',
  'Repetitive motions required?': '¿Se requieren movimientos repetitivos?',
  'Driving required?': '¿Se requiere manejar?',
  'If yes, hours per day': 'Si es sí, horas por día',
  'WC Insurance Carrier Name': 'Nombre de la Aseguradora de Compensación Laboral',
  'WC Carrier Phone': 'Teléfono de la Aseguradora de CL',
  'WC Claim Number': 'Número de Reclamo de CL',
  'Claim Status': 'Estado del Reclamo',
  'If denied, reason': 'Si fue denegado, razón',
  'Has treatment been authorized?': '¿Se ha autorizado el tratamiento?',
  'Do you have a WC attorney?': '¿Tiene un abogado de compensación laboral?',
  'WC Attorney Name': 'Nombre del Abogado de CL',
  'WC Attorney Firm': 'Bufete del Abogado de CL',
  'WC Attorney Phone': 'Teléfono del Abogado de CL',
  'Authorized Treating Physician (ATP) Name': 'Nombre del Médico Autorizado (ATP)',
  'ATP Phone': 'Teléfono del ATP',

  // ===== FAMILY HISTORY =====
  'No significant family history': 'Sin historial familiar significativo',
  'Diabetes': 'Diabetes',
  'Type(s) of cancer in family': 'Tipo(s) de cáncer en la familia',
  'Arthritis / Joint Problems': 'Artritis / Problemas de Articulaciones',
  'Osteoporosis': 'Osteoporosis',
  'Mental Health Conditions': 'Condiciones de Salud Mental',
  'Spinal Problems (disc, scoliosis, stenosis)': 'Problemas de Columna (disco, escoliosis, estenosis)',

  // ===== SECTION HEADERS =====
  'Motor Vehicle Accident Details': 'Detalles del Accidente de Vehículo',
  'Lien Agreement': 'Acuerdo de Gravamen',
  'Parties to This Agreement': 'Partes de Este Acuerdo',
  'Injury Information': 'Información de la Lesión',
  'Authorization for Release of Protected Health Information': 'Autorización para Divulgar Información de Salud Protegida',
  'Consent for Treatment of a Minor': 'Consentimiento para Tratamiento de un Menor',
  'Work Status': 'Estado Laboral',
  'Employer Information': 'Información del Empleador',
  "Workers' Compensation Insurance": 'Seguro de Compensación Laboral',
  'Other Medical Providers Since Accident': 'Otros Proveedores Médicos Desde el Accidente',
  'Prior Injuries': 'Lesiones Previas',
  'Instructions': 'Instrucciones',

  // ===== OPTIONS =====
  'Domestic Partner': 'Pareja Doméstica',
  'Text Message': 'Mensaje de Texto',
  'Employed Full-Time': 'Empleado(a) Tiempo Completo',
  'Employed Part-Time': 'Empleado(a) Medio Tiempo',
  'Self-Employed': 'Trabajador(a) Independiente',
  'Unemployed': 'Desempleado(a)',
  'Student': 'Estudiante',
  'Retired': 'Jubilado(a)',
  'Disabled': 'Discapacitado(a)',
  'Homemaker': 'Ama de Casa',
  'Insurance': 'Seguro',
  'Self-Pay (No Insurance)': 'Pago Propio (Sin Seguro)',
  'Personal Injury / Attorney Lien': 'Lesión Personal / Gravamen de Abogado',
  "Workers' Compensation": 'Compensación Laboral',
  'Self': 'Uno Mismo',
  'Spouse': 'Cónyuge',
  'Child': 'Hijo(a)',
  'Parent': 'Padre/Madre',
  'Guardian': 'Tutor',
  'Google Search': 'Búsqueda en Google',
  'Facebook / Social Media': 'Facebook / Redes Sociales',
  'Friend or Family': 'Amigo o Familiar',
  'Attorney Referral': 'Referencia de Abogado',
  'Insurance Directory': 'Directorio de Seguros',
  'Drive By / Walk In': 'Pasaba por Aquí / Entré Sin Cita',
  'Community Event': 'Evento Comunitario',
  'Another Doctor': 'Otro Doctor',
  'Current Patient Referral': 'Referencia de un Paciente Actual',
  'Left Arm / Elbow': 'Brazo / Codo Izquierdo',
  'Right Arm / Elbow': 'Brazo / Codo Derecho',
  'Left Wrist / Hand': 'Muñeca / Mano Izquierda',
  'Right Wrist / Hand': 'Muñeca / Mano Derecha',
  'Left Ankle / Foot': 'Tobillo / Pie Izquierdo',
  'Right Ankle / Foot': 'Tobillo / Pie Derecho',
  'Cramping': 'Calambres',
  'Pressure': 'Presión',
  'Sitting': 'Sentarse',
  'Standing': 'Estar de Pie',
  'Walking': 'Caminar',
  'Bending Forward': 'Inclinarse Hacia Adelante',
  'Bending Backward': 'Inclinarse Hacia Atrás',
  'Lifting': 'Levantar Objetos',
  'Twisting': 'Girar el Cuerpo',
  'Lying Down': 'Acostarse',
  'Lying on Side': 'Acostarse de Lado',
  'Getting Up': 'Levantarse',
  'Reaching Overhead': 'Alcanzar Hacia Arriba',
  'Working': 'Trabajar',
  'Coughing / Sneezing': 'Toser / Estornudar',
  'Deep Breathing': 'Respiración Profunda',
  'Turning Head': 'Girar la Cabeza',
  'Stress': 'Estrés',
  'Weather Changes': 'Cambios de Clima',
  'End of Day': 'Final del Día',
  'Nothing Specific': 'Nada Específico',
  'Rest': 'Descanso',
  'Ice': 'Hielo',
  'Heat': 'Calor',
  'Stretching': 'Estiramientos',
  'Movement / Walking': 'Movimiento / Caminar',
  'Over-the-Counter Medication': 'Medicamento Sin Receta',
  'Prescription Medication': 'Medicamento con Receta',
  'Massage': 'Masaje',
  'Chiropractic Adjustment': 'Ajuste Quiropráctico',
  'Changing Position': 'Cambiar de Posición',
  'Bracing / Support': 'Soporte / Faja',
  'Nothing Helps': 'Nada Ayuda',
  'None of the above': 'Ninguno de los anteriores',
  'None': 'Ninguno',
  'Chiropractic': 'Quiropráctica',
  'Physical Therapy': 'Terapia Física',
  'Acupuncture': 'Acupuntura',
  'Injections / Cortisone': 'Inyecciones / Cortisona',
  'Surgery': 'Cirugía',
  'OTC Medication': 'Medicamento Sin Receta',
  'Emergency Room': 'Sala de Emergencias',
  'Urgent Care': 'Atención Urgente',
  'Primary Care Doctor': 'Médico de Atención Primaria',
  'Specialist': 'Especialista',
  'Somewhat': 'Algo',
  'N/A — no previous treatment': 'N/A — sin tratamiento previo',
  'Diversified (manual)': 'Diversificada (manual)',
  'Activator (instrument)': 'Activador (instrumento)',
  'Thompson (drop table)': 'Thompson (mesa con caída)',
  'Flexion-Distraction': 'Flexión-Distracción',
  'Not Sure': 'No Estoy Seguro(a)',
  'Yes — X-rays': 'Sí — Rayos X',
  'Yes — MRI': 'Sí — Resonancia Magnética',
  'Yes — CT Scan': 'Sí — Tomografía',
  'Yes — Multiple types': 'Sí — Varios tipos',
  'Never had anesthesia': 'Nunca he tenido anestesia',
  'Mother': 'Madre',
  'Father': 'Padre',
  'Sibling(s)': 'Hermano(s)',
  'Grandparent(s)': 'Abuelo(s)',
  'Former (quit)': 'Antes (dejó de fumar)',
  'Former': 'Antes',
  'Occasionally (1-3/week)': 'Ocasionalmente (1-3/semana)',
  'Regularly (4+/week)': 'Regularmente (4+/semana)',
  'Occasionally (1-2x/week)': 'Ocasionalmente (1-2x/semana)',
  'Regularly (3-4x/week)': 'Regularmente (3-4x/semana)',
  'Daily': 'Diariamente',
  'Running': 'Correr',
  'Weight Training': 'Pesas',
  'Swimming': 'Natación',
  'Yoga / Pilates': 'Yoga / Pilates',
  'Cycling': 'Ciclismo',
  'Sports': 'Deportes',
  'Sedentary (desk/computer)': 'Sedentario (escritorio/computadora)',
  'Light (mostly standing/walking)': 'Ligero (mayormente de pie/caminando)',
  'Moderate (some lifting up to 50 lbs)': 'Moderado (algo de carga hasta 50 lbs)',
  'Heavy (frequent lifting over 50 lbs)': 'Pesado (carga frecuente de más de 50 lbs)',
  'Very Heavy (construction, labor)': 'Muy Pesado (construcción, trabajo manual)',
  'Back': 'Boca Arriba',
  'Left Side': 'Lado Izquierdo',
  'Right Side': 'Lado Derecho',
  'Stomach': 'Boca Abajo',
  'Varies': 'Varía',
  'Family': 'Familia',
  'Financial': 'Financiero',
  'Health': 'Salud',
  'Unexplained weight loss or gain': 'Pérdida o aumento de peso sin explicación',
  'Fever or chills': 'Fiebre o escalofríos',
  'Fatigue / low energy': 'Fatiga / baja energía',
  'Night sweats': 'Sudoración nocturna',
  'Loss of appetite': 'Pérdida de apetito',
  'Frequent headaches': 'Dolores de cabeza frecuentes',
  'Vision changes': 'Cambios en la visión',
  'Hearing loss': 'Pérdida de audición',
  'Ringing in ears (tinnitus)': 'Zumbido en los oídos (tinnitus)',
  'Sinus problems': 'Problemas de sinusitis',
  'Frequent sore throat': 'Dolor de garganta frecuente',
  'Difficulty swallowing': 'Dificultad para tragar',
  'Chest pain or pressure': 'Dolor o presión en el pecho',
  'Heart palpitations': 'Palpitaciones del corazón',
  'Swelling in legs or ankles': 'Hinchazón en piernas o tobillos',
  'Shortness of breath with exertion': 'Falta de aire con esfuerzo',
  'History of blood clots': 'Historial de coágulos de sangre',
  'Chronic cough': 'Tos crónica',
  'Wheezing': 'Sibilancias',
  'Shortness of breath at rest': 'Falta de aire en reposo',
  'Difficulty breathing lying down': 'Dificultad para respirar acostado(a)',
  'Nausea or vomiting': 'Náuseas o vómitos',
  'Diarrhea or constipation': 'Diarrea o estreñimiento',
  'Blood in stool': 'Sangre en las heces',
  'Heartburn / acid reflux': 'Acidez / reflujo ácido',
  'Abdominal pain': 'Dolor abdominal',
  'Painful urination': 'Dolor al orinar',
  'Frequent urination': 'Orinar frecuentemente',
  'Blood in urine': 'Sangre en la orina',
  'Incontinence': 'Incontinencia',
  'Joint pain elsewhere': 'Dolor en otras articulaciones',
  'Muscle weakness': 'Debilidad muscular',
  'Joint swelling': 'Hinchazón de articulaciones',
  'Morning stiffness > 30 minutes': 'Rigidez matutina > 30 minutos',
  'Muscle spasms': 'Espasmos musculares',
  'Numbness / tingling elsewhere': 'Adormecimiento / hormigueo en otras áreas',
  'Weakness': 'Debilidad',
  'Dizziness / lightheadedness': 'Mareos / aturdimiento',
  'Balance problems': 'Problemas de equilibrio',
  'Tremors': 'Temblores',
  'Fainting': 'Desmayos',
  'Memory problems': 'Problemas de memoria',
  'Rashes': 'Erupciones / Sarpullido',
  'Easy bruising': 'Moretones fáciles',
  'Slow wound healing': 'Cicatrización lenta',
  'Changes in moles': 'Cambios en lunares',
  'Mood changes': 'Cambios de humor',
  'Difficulty concentrating': 'Dificultad para concentrarse',
  'Excessive thirst': 'Sed excesiva',
  'Heat or cold intolerance': 'Intolerancia al calor o frío',
  'Excessive sweating': 'Sudoración excesiva',
  'Hair loss': 'Pérdida de cabello',
  'Reduce / eliminate pain': 'Reducir / eliminar el dolor',
  'Improve mobility / flexibility': 'Mejorar la movilidad / flexibilidad',
  'Improve posture': 'Mejorar la postura',
  'Return to work': 'Regresar al trabajo',
  'Return to sports / activities': 'Regresar a deportes / actividades',
  'Prevent future problems': 'Prevenir problemas futuros',
  'Wellness / maintenance care': 'Bienestar / cuidado de mantenimiento',
  'Reduce medication use': 'Reducir el uso de medicamentos',
  'Improve sleep': 'Mejorar el sueño',
  'Improve quality of life': 'Mejorar la calidad de vida',
  'As recommended by the doctor': 'Según lo recomiende el doctor',
  'Maybe': 'Tal vez',
  'Possibly': 'Posiblemente',
  'Not applicable': 'No aplica',
  'Motor Vehicle Accident': 'Accidente de Vehículo',
  'Slip and Fall': 'Resbalón y Caída',
  'Work Injury': 'Lesión de Trabajo',
  'Pedestrian': 'Peatón',
  'Bicycle': 'Bicicleta',
  'Motorcycle': 'Motocicleta',
  'Assault': 'Agresión',
  'Sports Injury': 'Lesión Deportiva',
  'Partial Fault': 'Culpa Parcial',
  'Rear-ended': 'Choque por Detrás',
  'Head-on': 'Choque Frontal',
  'Side impact — driver side': 'Impacto Lateral — Lado del Conductor',
  'Side impact — passenger side': 'Impacto Lateral — Lado del Pasajero',
  'T-bone': 'Impacto en T',
  'Rollover': 'Volcadura',
  'Multiple impacts': 'Impactos Múltiples',
  'Driver': 'Conductor',
  'Front passenger': 'Pasajero Delantero',
  'Rear left': 'Trasero Izquierdo',
  'Rear right': 'Trasero Derecho',
  'Rear middle': 'Trasero Centro',
  'Facing straight': 'Mirando al frente',
  'Turned left': 'Girado a la izquierda',
  'Turned right': 'Girado a la derecha',
  'Looking down': 'Mirando hacia abajo',
  'Looking up': 'Mirando hacia arriba',
  'Moving': 'En movimiento',
  'Stopped': 'Detenido',
  'Parked': 'Estacionado',
  'Minor (dents, scratches)': 'Menor (abolladuras, rayones)',
  'Moderate (panels damaged, drivable)': 'Moderado (paneles dañados, manejable)',
  'Severe (not drivable)': 'Severo (no manejable)',
  'Totaled': 'Pérdida Total',
  'With difficulty': 'Con dificultad',
  'Yes — by ambulance': 'Sí — en ambulancia',
  'Yes — on my own': 'Sí — por mi cuenta',
  'Neck pain': 'Dolor de cuello',
  'Back pain': 'Dolor de espalda',
  'Headaches': 'Dolores de cabeza',
  'Dizziness': 'Mareos',
  'Nausea': 'Náuseas',
  'Shoulder pain': 'Dolor de hombro',
  'Arm pain / numbness': 'Dolor / adormecimiento de brazo',
  'Leg pain / numbness': 'Dolor / adormecimiento de pierna',
  'Jaw pain (TMJ)': 'Dolor de mandíbula (ATM)',
  'Blurred vision': 'Visión borrosa',
  'Ringing in ears': 'Zumbido en los oídos',
  'Sleep problems': 'Problemas de sueño',
  'Anxiety / Emotional changes': 'Ansiedad / Cambios emocionales',
  'Fatigue': 'Fatiga',
  'Difficulty walking': 'Dificultad para caminar',
  'Immediately at the scene': 'Inmediatamente en la escena',
  'Within hours': 'En pocas horas',
  'Next day': 'Al día siguiente',
  'Within a few days': 'En pocos días',
  'Within a week': 'En una semana',
  'More than a week later': 'Más de una semana después',
  'Fluctuating': 'Fluctuante',
  'Yes — Full Duty': 'Sí — Trabajo Completo',
  'Yes — Light/Modified Duty': 'Sí — Trabajo Ligero/Modificado',
  'No — Unable to Work': 'No — No Puede Trabajar',
  'Not yet': 'Todavía no',
  'Attorney request': 'Solicitud del abogado',
  'Insurance company': 'Compañía de seguros',
  'Transfer to another provider': 'Transferencia a otro proveedor',
  'Personal / Patient request': 'Solicitud personal / del paciente',
  'Disability determination': 'Determinación de discapacidad',
  'Complete medical records': 'Registros médicos completos',
  'Office visit notes (SOAP notes)': 'Notas de consulta (notas SOAP)',
  'X-ray reports and images': 'Reportes e imágenes de rayos X',
  'Billing records / Ledger': 'Registros de facturación',
  'Lab results': 'Resultados de laboratorio',
  'Referral letters': 'Cartas de referencia',
  'Intake forms': 'Formularios de admisión',
  'Legal Guardian': 'Tutor Legal',
  'Stepparent': 'Padrastro/Madrastra',
  'Grandparent': 'Abuelo(a)',
  'Left Arm/Hand': 'Brazo/Mano Izquierda',
  'Right Arm/Hand': 'Brazo/Mano Derecha',
  'Left Hip/Leg': 'Cadera/Pierna Izquierda',
  'Right Hip/Leg': 'Cadera/Pierna Derecha',
  'Head': 'Cabeza',
  "Don't Know": 'No Sé',
  'Yes — ER': 'Sí — Emergencias',
  'Yes — Urgent Care': 'Sí — Atención Urgente',
  'Yes — Doctor': 'Sí — Doctor',
  'Self-Referred': 'Auto-referido',
  'Attorney': 'Abogado',
  'Other Doctor': 'Otro Doctor',
  'Yes — Reduced Hours': 'Sí — Horas Reducidas',
  'No — Totally Disabled': 'No — Totalmente Incapacitado',
  'Applied/Pending': 'Solicitado/Pendiente',
  'Partially': 'Parcialmente',
  'Full-Time': 'Tiempo Completo',
  'Part-Time': 'Medio Tiempo',
  'Temporary/Seasonal': 'Temporal/Estacional',
  'Contract': 'Contrato',
  'Light (standing/walking, lifting up to 20 lbs)': 'Ligero (de pie/caminando, carga hasta 20 lbs)',
  'Medium (lifting up to 50 lbs)': 'Medio (carga hasta 50 lbs)',
  'Heavy (lifting up to 100 lbs)': 'Pesado (carga hasta 100 lbs)',
  'Very Heavy (over 100 lbs)': 'Muy Pesado (más de 100 lbs)',
  'Accepted': 'Aceptado',
  'Denied': 'Denegado',
  'Pending': 'Pendiente',
  'Controverted': 'Controvertido',

  // ---- Intake trim 2026-07-09: new / relabeled fields ----
  'You may contact me by: (check all that apply)': 'Puede contactarme por: (marque todas las que apliquen)',
  'Voicemail': 'Mensaje de voz',
  'Text message': 'Mensaje de texto',
  'Have you EVER been diagnosed with or experienced any of the following? (check all that apply)': '¿ALGUNA VEZ le han diagnosticado o ha experimentado alguna de las siguientes condiciones? (marque todas las que apliquen)',
  'Do you have a pacemaker, defibrillator, or other implanted electronic device (e.g., spinal cord stimulator)?': '¿Tiene un marcapasos, desfibrilador u otro dispositivo electrónico implantado (por ejemplo, un estimulador de médula espinal)?',
  'Do you have any metal implants (e.g., joint replacement, rods, plates, screws)?': '¿Tiene algún implante metálico (por ejemplo, reemplazo articular, varillas, placas o tornillos)?',
  'Do you have a pacemaker or any metal implants?': '¿Tiene un marcapasos o algún implante metálico?',
  'If autoimmune disease, which one?': 'Si tiene enfermedad autoinmune, ¿cuál?',
  'Does anyone in your IMMEDIATE FAMILY (parents, siblings, grandparents) have a history of: (check all that apply)': '¿Alguien en su FAMILIA INMEDIATA (padres, hermanos, abuelos) tiene historial de: (marque todas las que apliquen)',
  'Details worth noting (which condition / which family member)': 'Detalles importantes (qué condición / qué familiar)',
  'No significant family history': 'Sin antecedentes familiares significativos',
  'Tobacco / Vape Use': 'Uso de Tabaco / Vapeo',
  'If current/former: what, how much, and for how many years?': 'Si actual/anterior: ¿qué, cuánto y por cuántos años?',
  'Over-the-Counter Medications / Vitamins / Supplements Used Regularly': 'Medicamentos sin receta / Vitaminas / Suplementos de uso regular',
  'Do you have any medication allergies?': '¿Tiene alergias a medicamentos?',
  'Primary Subscriber Name': 'Nombre del Suscriptor Principal',
  'Subscriber Date of Birth': 'Fecha de Nacimiento del Suscriptor',
};

/**
 * Translate a label or option string. Returns Spanish if available, else original.
 */
export function t(text, lang = 'en') {
  if (lang === 'en' || !text) return text;
  return LABEL_TRANSLATIONS[text] || text;
}

/**
 * Translate an array of options
 */
export function tOptions(options, lang = 'en') {
  if (lang === 'en' || !options) return options;
  return options.map(o => LABEL_TRANSLATIONS[o] || o);
}

/**
 * Get UI text
 */
export function tUI(key, lang = 'en') {
  return UI_TRANSLATIONS[lang]?.[key] || UI_TRANSLATIONS.en[key] || key;
}
